// ///////////////////////////////////////////////////////////////////////////////// //
// -----------------------  Rainfall Accumulation Tracker ------------------------   //
// --------------------- The code to generate the mapbox map ----------------------  //
//                                                                                   //
//  URL parameters available:                                                       //
//   - lat and lon: Sets the center of the map                                      //
//   - zoom: Sets the zoom level, range:[1 - 20] - defaults to 3.3                 //
//   - date: Selects a specific date. Format [yyyy_mm_dd] - defaults to latest      //
//                                                                                   //
// ///////////////////////////////////////////////////////////////////////////////// //

import { Child } from 'pym.js';
import * as d3 from 'd3';
import meta from './data/meta.json';

import Zion from './utils/Zion';

import './utils/polyfills';
import { stylePremium } from './utils/helpers';
import './styles/graphic.scss';

const mapboxgl = require('mapbox-gl');

const pkg = require('../package.json');

window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
stylePremium(CNNPolitics, CNNBusiness);

// Get URL parameters
let queryString = window.location.search;

try {
  const parentHref = window.parent.location.href;

  if (window.parent !== window && parentHref.includes('parent.html')) {
    queryString = window.parent.location.search || queryString;
  }
} catch (err) {
  // Cross-origin error, stick with current location
}

const urlParams = new URLSearchParams(queryString);

const date = urlParams.has('date') ? `_${urlParams.get('date')}` : '';
const url = `rainfall_accumulation_southeast_asia_7day${date}.json`;

const container = d3.select('#graphic');
container.html('');

container.append('div')
  .attr('id', 'loading')
  .attr('style', 'text-align: center; padding: 20px; color: #666;')
  .html('Loading rainfall data...');

container.append('div')
  .attr('id', 'map')
  .attr('style', 'width: 100%; height: 600px;');

mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

d3.json(url).then((dataArray) => {
  d3.select('#loading').remove();

  if (!dataArray || !dataArray[0] || !dataArray[0].data) {
    return;
  }

  const dataItem = dataArray[0];
  const { last_updated } = dataItem;

  if (meta.headline && meta.headline.trim()) {
    d3.select('#hed').text(meta.headline.trim());
  } else {
    d3.select('#hed').text('Rainfall accumulation in Southeast Asia');
  }

  if (meta.subhed && meta.subhed.trim()) {
    d3.select('#dek').html(meta.subhed.trim());
  } else {
    d3.select('#dek').text(`${last_updated}`);
  }

  if (meta.credit && meta.credit.trim()) {
    d3.select('p.credit').text(`Graphic: ${meta.credit.trim()}`);
  }

  d3.select('#timestamp').text(`Data as of ${last_updated}`);

  // Detect mobile devices - use media query for more reliable detection
  const isMobileByWidth = window.matchMedia('(max-width: 768px)').matches;
  const isMobileByUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isMobile = isMobileByWidth || isMobileByUA;

  const defaultMapCenter = isMobile ? [90, 5] : [100.5, 5]; // Mobile centered further west to show Sri Lanka
  const defaultZoom = isMobile ? 2.2 : 3.3; // More zoomed out on mobile

  const mapCenter = urlParams.has('lat') && urlParams.has('lon')
    ? [parseFloat(urlParams.get('lon')), parseFloat(urlParams.get('lat'))]
    : defaultMapCenter;
  const mapZoom = urlParams.has('zoom') ? parseFloat(urlParams.get('zoom')) : defaultZoom;

  const map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/cnndigital/cm2yzafi700tb01o063rvaunz',
    zoom: mapZoom,
    maxZoom: 18,
    minZoom: 1,
    center: mapCenter,
    maxBounds: null,
    bearing: 0,
    scrollZoom: false,
    dragRotate: false,
    dragPan: false,
    logoPosition: 'bottom-left',
    touchPitch: false,
    interactive: true,
  });

  // Explicitly set zoom and center immediately and after style loads to ensure it's applied
  // Some map styles may override initial zoom, so we set it explicitly
  map.setZoom(mapZoom);
  map.setCenter(mapCenter);

  map.once('style.load', () => {
    map.setZoom(mapZoom);
    map.setCenter(mapCenter);
  });

  // Function to update map zoom/center based on current window size
  const updateMapForWindowSize = () => {
    const currentIsMobileByWidth = window.matchMedia('(max-width: 768px)').matches;
    const currentIsMobile = currentIsMobileByWidth || isMobileByUA;

    const newDefaultCenter = currentIsMobile ? [100, 5] : [100.5, 5];
    const newDefaultZoom = currentIsMobile ? 2.2 : 3.5;

    // Only update if URL params don't override
    if (!urlParams.has('zoom')) {
      map.setZoom(newDefaultZoom);
    }
    if (!urlParams.has('lat') || !urlParams.has('lon')) {
      map.setCenter(newDefaultCenter);
    }
  };

  // Debounce resize handler to avoid too many updates
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateMapForWindowSize, 150);
  });

  d3.selectAll('.mapboxgl-ctrl-attrib-button')
    .style('display', 'none');

  map.on('load', () => {
    map.addSource('rainfall-layer', {
      type: 'raster',
      url: 'mapbox://cnndigital.rainfall-se-asia-7D11302330',
      tileSize: 256,
    });

    const mapStyle = map.getStyle();
    const mapLayers = mapStyle.layers;
    const firstSymbolLayer = mapLayers.find(layer => layer.type === 'symbol');
    const beforeLayer = firstSymbolLayer ? firstSymbolLayer.id : undefined;

    map.addLayer({
      id: 'rainfall-layer',
      type: 'raster',
      source: 'rainfall-layer',
      url: 'mapbox://cnndigital.rainfall-se-asia-7D11302330',
      tileSize: 256,
    }, beforeLayer);

    d3.json('world-land-sea-boundary50m.geojson').then((boundaryData) => {
      if (boundaryData && boundaryData.features && boundaryData.features.length > 0) {
        map.addSource('world-boundary', {
          type: 'geojson',
          data: boundaryData,
        });

        map.addLayer({
          id: 'world-boundary-layer',
          type: 'line',
          source: 'world-boundary',
          paint: {
            'line-color': '#929292',
            'line-width': 0.75,
            'line-opacity': 1,
          },
        }, beforeLayer);
      }
    }).catch(() => {
      // Error loading boundary data - fail silently
    });

    window.pymChild.sendHeight();
  });
}).catch(() => {
  // Error loading data - fail silently
});
